import type { 
  Device, 
  Health, 
  HealthData, 
  SleepStats, 
  UserShift, 
  ScheduleStats, 
  ScheduleResponse,
  Company,
  Group,
  User,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  CreateGroupRequest,
  UpdateGroupRequest,
  CreateUserRequest,
  ChangeRoleRequest,
  ApiResponse,
  Employee,
  CreateEmployeeRequest,
  UpdateEmployeeRequest,
  EmployeeFollowup,
  CreateEmployeeFollowupRequest,
  DeviceSwitchHistory
} from '../types';
import { getUserCompany } from './user';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 
  (import.meta.env.MODE == 'development' ? 'http://localhost:3031/api' : 'https://sw-backend.terretech.id/api'); // Sesuaikan dengan port yang benar
// Fungsi helper untuk fetch dengan error handling
async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    //console.log('Fetching:', url); // Untuk debugging

    // Get auth token from localStorage
    const token = localStorage.getItem('authToken');
    
    // Prepare headers
    const headers: Record<string, string> = {
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
    
    // Add custom headers from options if provided
    if (options?.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        if (typeof value === 'string') {
          headers[key] = value;
        }
      });
    }
    
    // Don't set Content-Type for FormData (browser will set it automatically with boundary)
    if (!(options?.body instanceof FormData) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    
    const response = await fetch(url, {
      ...options,
      headers,
    });
    
    if (!response.ok) {
      // Handle 401 Unauthorized - redirect to login
      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userInfo');
        window.location.href = '/login';
        throw new Error('Unauthorized - Please login again');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return (await response.json()) as T;
  } catch (error) {
    console.error('API request failed:', error);
    throw error; // Re-throw untuk handling di component
  }
}

// Fungsi-fungsi untuk mengambil data
export const api = {
  // Devices
  getAllDevices: async () => {
    try {
      const userCompany = getUserCompany();
      const response = await fetchApi<{success: boolean, data: Device[]}>(`/devices?companyOwner=${userCompany}`);
      
      // Server sudah memfilter berdasarkan query; kembalikan data apa adanya
      return response.data || [];
    } catch (error) {
      throw error;
    }
  },
  getDevicesByDate: async (date: string, companyOwnerParam?: string) => {
    const userCompany = companyOwnerParam || getUserCompany();
    const response = await fetchApi<{success: boolean, data: Device[]}>(`/devices?date=${date}&companyOwner=${userCompany}`);
    // Server sudah memfilter berdasarkan query; kembalikan data apa adanya
    return response.data || [];
  },
  getDevicesByCompany: async (companyOwnerParam?: string) => {
    const userCompany = companyOwnerParam || getUserCompany();
    const response = await fetchApi<{success: boolean, data: Device[]}>(`/devices?companyOwner=${userCompany}`);
    // Server sudah memfilter berdasarkan query; kembalikan data apa adanya
    return response.data || [];
  },
  getDevice: async (id: string) => {
    const response = await fetchApi<{success: boolean, data: Device}>(`/devices/${id}`);
    return response.data;
  },
  createDevice: async (data: { idEmployee?: string; name: string; mac: string; status?: string; company_id?: string; group_id?: string }) => {
    const response = await fetchApi<ApiResponse<Device>>('/devices', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },
  updateDevice: async (id: string, data: Partial<{ idEmployee: string; name: string; mac: string; status: string }>) => {
    const response = await fetchApi<ApiResponse<Device>>(`/devices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },
  assignDeviceToGroup: async (deviceId: string, groupId: string) => {
    const response = await fetchApi<ApiResponse<Device>>(`/devices/${deviceId}/assign-group`, {
      method: 'PUT',
      body: JSON.stringify({ group_id: groupId }),
    });
    return response.data;
  },

  // Health Data
  getAllHealthData: async () => {
    const userCompany = getUserCompany();
    const response = await fetchApi<{success: boolean, data: HealthData[]}>(`/health?companyOwner=${userCompany}`);
    return response.data || [];
  },
  getHealthDataByDevice: async (deviceId: string) => {
    const userCompany = getUserCompany();
    const response = await fetchApi<{success: boolean, data: HealthData[]}>(`/health/device/${deviceId}?companyOwner=${userCompany}`);
    return response.data || [];
  },
  getHealthByDeviceId: async (deviceId: string, startDate?: string, endDate?: string) => {
    const userCompany = getUserCompany();
    const query = new URLSearchParams();
    if (startDate) query.append('start_date', startDate);
    if (endDate) query.append('end_date', endDate);
    query.append('companyOwner', userCompany);
    const response = await fetchApi<{success: boolean, data: Health[]}>(`/health/device/${deviceId}${query.toString() ? `?${query.toString()}` : ''}`);
    return response.data || [];
  },

  // Sleep Data
  // Cache untuk menyimpan hasil permintaan API
  _sleepDataCache: new Map<string, {data: any, timestamp: number}>(),
  
  getAllSleepData: async (deviceIds: string, date?: string) => {
    // Use new API /sleep/statsByDate for each device
    if (!deviceIds || !date) {
      return [];
    }
    
    // Buat cache key berdasarkan deviceIds dan date
    const cacheKey = `${deviceIds}_${date}`;
    
    // Cek apakah data ada di cache dan masih valid (cache valid selama 5 menit)
    const cachedData = api._sleepDataCache.get(cacheKey);
    const now = Date.now();
    if (cachedData && (now - cachedData.timestamp < 5 * 60 * 1000)) {
      console.log('Using cached sleep data for', cacheKey);
      return cachedData.data;
    }
    
    const deviceIdArray = deviceIds.split(',').filter(id => id.trim());
    const sleepDataPromises = deviceIdArray.map(async (deviceId) => {
      try {
        const data = await api.getDeviceSleepData(deviceId.trim(), date);
        // Convert new API response to old format for compatibility
        return {
          deviceId: data.deviceId || deviceId,
          date: data.date || date,
          sleepTime: data.sleepTime || 0,
          sleepTotalTime: data.sleepTime || 0, // For backward compatibility
          originalSleepTime: data.originalSleepTime || 0,
          sleepQuality: data.sleepQuality || 0,
          heartRate: data.heartRate || { min: 0, max: 0 },
          bloodOxygen: data.bloodOxygen || { min: 0, max: 0 },
          sleepLogs: data.sleepLogs || []
        };
      } catch (error) {
        console.error(`Error fetching sleep data for device ${deviceId}:`, error);
        return {
          deviceId: deviceId,
          date: date,
          sleepTime: 0,
          sleepTotalTime: 0,
          originalSleepTime: 0,
          sleepQuality: 0,
          heartRate: { min: 0, max: 0 },
          bloodOxygen: { min: 0, max: 0 },
          sleepLogs: []
        };
      }
    });
    
    const results = await Promise.all(sleepDataPromises);
    
    // Simpan hasil di cache
    api._sleepDataCache.set(cacheKey, {
      data: results,
      timestamp: now
    });
    
    return results;
  },
  getDeviceSleepData: async (deviceId: string, date: string, tz: string = '+8') => {
    // Buat cache key berdasarkan deviceId, date, dan tz
    const cacheKey = `${deviceId}_${date}_${tz}`;
    
    // Cek apakah data ada di cache dan masih valid (cache valid selama 5 menit)
    const cachedData = api._sleepDataCache.get(cacheKey);
    const now = Date.now();
    if (cachedData && (now - cachedData.timestamp < 5 * 60 * 1000)) {
      console.log('Using cached device sleep data for', cacheKey);
      return cachedData.data;
    }
    
    const query = new URLSearchParams();
    query.append('deviceId', deviceId);
    query.append('date', date);
    query.append('tz', tz);
    //console.log('getDeviceSleepData', deviceId, date, tz);
    const response = await fetchApi<{success: boolean, data: any}>(`/sleep/statsByDate?${query.toString()}`);
    const data = response.data || {};
    
    // Simpan hasil di cache
    api._sleepDataCache.set(cacheKey, {
      data: data,
      timestamp: now
    });
    
    return data;
  },
  getSleepStatsByDate: async (deviceId: string, date: string, tz: string = '+8') => {
    // Buat cache key berdasarkan deviceId, date, dan tz
    const cacheKey = `${deviceId}_${date}_${tz}`;
    
    // Cek apakah data ada di cache dan masih valid (cache valid selama 5 menit)
    const cachedData = api._sleepDataCache.get(cacheKey);
    const now = Date.now();
    if (cachedData && (now - cachedData.timestamp < 5 * 60 * 1000)) {
      console.log('Using cached sleep stats for', cacheKey);
      return cachedData.data;
    }
    
    const response = await fetchApi<{success: boolean, data: {sleepTime: number}}>(`/sleep/statsByDate?deviceId=${deviceId}&date=${date}&tz=${tz}`);
    const data = response.data;
    
    // Simpan hasil di cache
    api._sleepDataCache.set(cacheKey, {
      data: data,
      timestamp: now
    });
    
    return data;
  },
  getSleepStats: async (deviceId: string) => {
    const userCompany = getUserCompany();
    const response = await fetchApi<{success: boolean, data: SleepStats}>(`/sleep/stats/${deviceId}?companyOwner=${userCompany}`);
    return response.data;
  },

  // User Shifts / Schedule
  getAllUserShifts: async (filters?: { date?: string; schedule_type?: string; device_id?: string; idEmployee?: string }) => {
    const userCompany = getUserCompany();
    const query = new URLSearchParams();
    if (filters?.date) query.append('date', filters.date);
    if (filters?.schedule_type) query.append('schedule_type', filters.schedule_type);
    if (filters?.device_id) query.append('device_id', filters.device_id);
    if (filters?.idEmployee) query.append('idEmployee', filters.idEmployee);
    query.append('companyOwner', userCompany);
    const response = await fetchApi<{success: boolean, data: UserShift[]}>(`/user-shifts${query.toString() ? `?${query.toString()}` : ''}`);
    return response.data || [];
  },
  getUserShiftsByDate: async (date: string) => {
    const userCompany = getUserCompany();
    const response = await fetchApi<ScheduleResponse>(`/user-shifts/date/${date}?companyOwner=${userCompany}`);
    return response;
  },
  getUserShift: async (id: string) => {
    const userCompany = getUserCompany();
    const response = await fetchApi<UserShift>(`/user-shifts/${id}?companyOwner=${userCompany}`);
    return response;
  },
  getScheduleStats: async (date?: string) => {
    const userCompany = getUserCompany();
    const query = new URLSearchParams();
    if (date) query.append('date', date);
    query.append('companyOwner', userCompany);
    const response = await fetchApi<{ date: string; statistics: ScheduleStats; details: any[] }>(`/user-shifts/stats${query.toString() ? `?${query.toString()}` : ''}`);
    return response;
  },
  createUserShift: (data: { device_id?: string; idEmployee?: string; date: string; schedule_type: string }) => 
    fetchApi<{ message: string; data: UserShift }>('/user-shifts', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateUserShift: (data: { device_id?: string; idEmployee?: string; date: string; schedule_type: string }) => 
    fetchApi<{ message: string; data: UserShift }>('/user-shifts', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteUserShift: (data: { device_id?: string; idEmployee?: string; date: string }) => 
    fetchApi<{ message: string; data: UserShift }>('/user-shifts', {
      method: 'DELETE',
      body: JSON.stringify(data),
    }),

  // Companies
  getAllCompanies: async () => {
    const response = await fetchApi<ApiResponse<Company[]>>('/companies');
    return response.data || [];
  },
  getCompanyById: async (id: string) => {
    const response = await fetchApi<ApiResponse<Company>>(`/companies/${id}`);
    return response.data;
  },
  createCompany: async (data: CreateCompanyRequest) => {
    const response = await fetchApi<ApiResponse<Company>>('/companies', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },
  updateCompany: async (id: string, data: UpdateCompanyRequest) => {
    const response = await fetchApi<ApiResponse<Company>>(`/companies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },
  deleteCompany: async (id: string) => {
    const response = await fetchApi<ApiResponse<void>>(`/companies/${id}`, {
      method: 'DELETE',
    });
    return response;
  },

  // Groups
  getAllGroups: async (companyId?: string) => {
    const query = companyId ? `?company_id=${companyId}` : '';
    const response = await fetchApi<ApiResponse<Group[]>>(`/groups${query}`);
    return response.data || [];
  },
  getGroupById: async (id: string) => {
    const response = await fetchApi<ApiResponse<Group>>(`/groups/${id}`);
    return response.data;
  },
  createGroup: async (data: CreateGroupRequest) => {
    const response = await fetchApi<ApiResponse<Group>>('/groups', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },
  updateGroup: async (id: string, data: UpdateGroupRequest) => {
    const response = await fetchApi<ApiResponse<Group>>(`/groups/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },
  deleteGroup: async (id: string) => {
    const response = await fetchApi<ApiResponse<void>>(`/groups/${id}`, {
      method: 'DELETE',
    });
    return response;
  },
  getGroupDevices: async (groupId: string) => {
    const response = await fetchApi<ApiResponse<Device[]>>(`/groups/${groupId}/devices`);
    return response.data || [];
  },
  removeDeviceFromGroup: async (deviceId: string) => {
    const response = await fetchApi<ApiResponse<Device>>(`/devices/${deviceId}/remove-group`, {
      method: 'PUT',
    });
    return response.data;
  },

  // Device IMEI
  getDeviceIMEIBatch: async (deviceIds: string[]) => {
    const query = deviceIds.join(',');
    const response = await fetchApi<{ success: boolean; data: Record<string, Array<{ id: string; device_id: string; imei: string; data_source_name: string; package_name?: string; created_at: string; updated_at: string }>> }>(`/devices/imei/batch?deviceIds=${query}`);
    return response.data || {};
  },

  // Users
  getAllUsers: async (filters?: { company_id?: string; group_id?: string; role?: string }) => {
    const query = new URLSearchParams();
    if (filters?.company_id) query.append('company_id', filters.company_id);
    if (filters?.group_id) query.append('group_id', filters.group_id);
    if (filters?.role) query.append('role', filters.role);
    const response = await fetchApi<ApiResponse<User[]>>(`/users${query.toString() ? `?${query.toString()}` : ''}`);
    return response.data || [];
  },
  getUserById: async (id: string) => {
    const response = await fetchApi<ApiResponse<User>>(`/users/${id}`);
    return response.data;
  },
  createUser: async (data: CreateUserRequest) => {
    const response = await fetchApi<ApiResponse<User>>('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },
  updateUser: async (id: string, data: Partial<CreateUserRequest>) => {
    const response = await fetchApi<ApiResponse<User>>(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },
  deleteUser: async (id: string) => {
    const response = await fetchApi<ApiResponse<void>>(`/users/${id}`, {
      method: 'DELETE',
    });
    return response;
  },
  changeUserRole: async (userId: string, data: ChangeRoleRequest) => {
    const response = await fetchApi<ApiResponse<User>>(`/users/${userId}/change-role`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },
  changeUserPassword: async (userId: string, data: { password: string }) => {
    const response = await fetchApi<ApiResponse<User>>(`/users/${userId}/change-password`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },
  sendResetPasswordLink: async (userId: string) => {
    const response = await fetchApi<ApiResponse<{ message: string }>>(`/users/${userId}/send-reset-password`, {
      method: 'POST',
    });
    return response;
  },

  // Summary Health
  getSummaryHealth: async (params?: { page?: number; limit?: number; date?: string; companyOwner?: string }) => {
    const userCompany = params?.companyOwner || getUserCompany();
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.date) query.append('date', params.date);
    query.append('companyOwner', userCompany);
    
    const response = await fetchApi<{
      success: boolean;
      data: {
        devices: Array<{
          deviceId: string;
          deviceName: string | null;
          idEmployee: string | null;
          vitalSigns: {
            heartRate: number | null;
            bloodOxygen: number | null;
            stressLevel: number | null;
          };
          sleepData: {
            sleepTime: number;
            sleepQuality: number;
            heartRate: { min: number; max: number };
            bloodOxygen: { min: number; max: number };
          };
          schedule: {
            scheduleType: "day" | "night" | "off" | "fullday" | null;
            filteredSleepTime: string;
          };
          sleepStatus: "normal" | "abnormal" | "nodata";
        }>;
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      };
    }>(`/summary-health${query.toString() ? `?${query.toString()}` : ''}`);
    
    return response;
  },

  // Data Availability
  getMonthlyDataAvailability: async (month: string, companyOwner?: string) => {
    const query = new URLSearchParams();
    query.append('month', month);
    if (companyOwner) query.append('companyOwner', companyOwner);
    
    const response = await fetchApi<{
      success: boolean;
      data: Record<string, { hasSleep: boolean; hasVital: boolean }>;
      month: string;
      companyOwner?: string;
      deviceCount: number;
    }>(`/data-availability?${query.toString()}`);
    
    return response.data || {};
  },

  // Server Time
  getServerTime: async () => {
    const response = await fetchApi<{
      success: boolean;
      data: {
        timestamp: string;
        timezone: string;
        unixTimestamp: number;
      };
    }>('/server-time');
    return response.data;
  },

  // Employees
  getAllEmployees: async (filters?: { page?: number; limit?: number; search?: string; status?: string; sortBy?: string; sortOrder?: 'asc' | 'desc' }) => {
    const query = new URLSearchParams();
    if (filters?.page) query.append('page', filters.page.toString());
    if (filters?.limit) query.append('limit', filters.limit.toString());
    if (filters?.search) query.append('search', filters.search);
    if (filters?.status) query.append('status', filters.status);
    if (filters?.sortBy) query.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) query.append('sortOrder', filters.sortOrder);
    const response = await fetchApi<{ success: boolean; data: Employee[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/employees${query.toString() ? `?${query.toString()}` : ''}`);
    return {
      employees: response.data,
      pagination: response.pagination
    };
  },
  getEmployeeById: async (id: string) => {
    const response = await fetchApi<ApiResponse<Employee>>(`/employees/${id}`);
    return response.data;
  },
  createEmployee: async (data: CreateEmployeeRequest) => {
    const response = await fetchApi<ApiResponse<Employee>>('/employees', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },
  updateEmployee: async (id: string, data: UpdateEmployeeRequest) => {
    const response = await fetchApi<ApiResponse<Employee>>(`/employees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },
  deleteEmployee: async (id: string) => {
    const response = await fetchApi<ApiResponse<void>>(`/employees/${id}`, {
      method: 'DELETE',
    });
    return response;
  },

  // System Parameters
  getSystemParameters: async (companyId?: string) => {
    const query = companyId ? `?companyId=${companyId}` : '';
    const response = await fetchApi<{ success: boolean; data: any }>(`/system-parameters${query}`);
    return response.data;
  },

  // Employee Followups
  getFollowupsByDate: async (date: string) => {
    const response = await fetchApi<{ success: boolean; data: Record<string, EmployeeFollowup> }>(`/employee-followups/date/${date}`);
    return response.data || {};
  },
  getAllFollowups: async (filters?: { idEmployee?: string; date?: string; startDate?: string; endDate?: string; followup_type?: string }) => {
    const query = new URLSearchParams();
    if (filters?.idEmployee) query.append('idEmployee', filters.idEmployee);
    if (filters?.date) query.append('date', filters.date);
    if (filters?.startDate) query.append('startDate', filters.startDate);
    if (filters?.endDate) query.append('endDate', filters.endDate);
    if (filters?.followup_type) query.append('followup_type', filters.followup_type);
    const response = await fetchApi<{ success: boolean; data: EmployeeFollowup[] }>(`/employee-followups${query.toString() ? `?${query.toString()}` : ''}`);
    return response.data || [];
  },
  getFollowupById: async (id: string) => {
    const response = await fetchApi<ApiResponse<EmployeeFollowup>>(`/employee-followups/${id}`);
    return response.data;
  },
  upsertFollowup: async (data: CreateEmployeeFollowupRequest) => {
    const response = await fetchApi<ApiResponse<EmployeeFollowup>>('/employee-followups', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },
  deleteFollowup: async (id: string) => {
    const response = await fetchApi<ApiResponse<void>>(`/employee-followups/${id}`, {
      method: 'DELETE',
    });
    return response;
  },

  // Anomaly Detection
  getAnomalyData: async (date: string) => {
    const userCompany = getUserCompany();
    const query = new URLSearchParams();
    query.append('date', date);
    query.append('companyOwner', userCompany);
    const response = await fetchApi<{
      success: boolean;
      data: {
        date: string;
        totalDevices: number;
        anomalyCount: number;
        rows: Array<{
          idEmployee: string;
          Nama: string;
          Jabatan: string | null;
          schedule_type: string;
          shift_date: string;
          window_start: string;
          window_end: string;
          heart_rate_count: number;
          heart_rate_min: number | null;
          heart_rate_max: number | null;
          heart_rate_avg: number | null;
          sleep_motion_count: number;
          heart_rate_anomaly: boolean;
          sleep_anomaly: boolean;
          has_anomaly: boolean;
        }>;
      };
    }>(`/anomaly?${query.toString()}`);
    return response.data;
  },

  // Offline Devices Detection
  getOfflineDevices: async (date: string) => {
    const userCompany = getUserCompany();
    const query = new URLSearchParams();
    query.append('date', date);
    query.append('companyOwner', userCompany);
    const response = await fetchApi<{
      success: boolean;
      data: {
        date: string;
        totalDevices: number;
        offlineCount: number;
        rows: Array<{
          idEmployee: string;
          Nama: string;
          Jabatan: string | null;
          schedule_type: string;
          shift_date: string;
          window_start: string;
          window_end: string;
          heart_rate_count: number;
          sleep_motion_count: number;
          blood_oxygen_count: number;
          pressure_count: number;
          is_offline: boolean;
        }>;
      };
    }>(`/offline-devices?${query.toString()}`);
    return response.data;
  },

  // Generic HTTP methods for custom endpoints
  post: async <T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<{ data: T; success: boolean }> => {
    const isFormData = data instanceof FormData;
    const response = await fetchApi<{ data: T; success: boolean }>(endpoint, {
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
      ...options,
    });
    return response;
  },

  get: async <T = any>(endpoint: string, options?: RequestInit): Promise<{ data: T; success: boolean }> => {
    const response = await fetchApi<{ data: T; success: boolean }>(endpoint, {
      method: 'GET',
      ...options,
    });
    return response;
  },

  // Device Switch History
  getDeviceSwitchHistory: async (filters?: { device_id?: string; employee_id?: string; action_type?: string; start_date?: string; end_date?: string; page?: number; limit?: number }) => {
    const query = new URLSearchParams();
    if (filters?.device_id) query.append('device_id', filters.device_id);
    if (filters?.employee_id) query.append('employee_id', filters.employee_id);
    if (filters?.action_type) query.append('action_type', filters.action_type);
    if (filters?.start_date) query.append('start_date', filters.start_date);
    if (filters?.end_date) query.append('end_date', filters.end_date);
    if (filters?.page) query.append('page', filters.page.toString());
    if (filters?.limit) query.append('limit', filters.limit.toString());
    const response = await fetchApi<{ success: boolean; data: DeviceSwitchHistory[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/device-switch-history${query.toString() ? `?${query.toString()}` : ''}`);
    return {
      data: response.data,
      pagination: response.pagination
    };
  },
  getDeviceSwitchHistoryByDeviceId: async (deviceId: string, limit: number = 10) => {
    const response = await fetchApi<{ success: boolean; data: DeviceSwitchHistory[] }>(`/device-switch-history/device/${deviceId}?limit=${limit}`);
    return response.data || [];
  },
  getDeviceSwitchHistoryByEmployeeId: async (employeeId: string, limit: number = 10) => {
    const response = await fetchApi<{ success: boolean; data: DeviceSwitchHistory[] }>(`/device-switch-history/employee/${employeeId}?limit=${limit}`);
    return response.data || [];
  },
  getDeviceSwitchHistoryById: async (id: string) => {
    const response = await fetchApi<{ success: boolean; data: DeviceSwitchHistory }>(`/device-switch-history/${id}`);
    return response.data;
  },
  deleteDeviceSwitchHistory: async (id: string) => {
    const response = await fetchApi<{ success: boolean; message: string }>(`/device-switch-history/${id}`, {
      method: 'DELETE',
    });
    return response;
  },
};
